import { supabase } from '../lib/supabase';

const rpcError = (error, fallback) => {
  if (!error) return null;
  if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
    return new Error('Summative examination booking is not installed in the database yet. Apply migration 20260101000044 and refresh.');
  }
  return new Error(error.message || fallback);
};

export const summativeExamService = {
  async getBookingWindow(academicYearId, semesterId) {
    if (!academicYearId || !semesterId) {
      return { configured: false, booking_enabled: true, opens_at: null, closes_at: null, status: 'unconfigured', can_book: false };
    }
    const { data, error } = await supabase.rpc('get_summative_exam_booking_window', {
      target_year: academicYearId,
      target_semester: semesterId,
    });
    if (error) {
      // Keep older deployments usable until the period-window migration is
      // applied; the database trigger remains the final authority.
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        return { configured: false, booking_enabled: true, opens_at: null, closes_at: null, status: 'legacy', can_book: true };
      }
      throw error;
    }
    return data || { configured: false, booking_enabled: true, opens_at: null, closes_at: null, status: 'unconfigured', can_book: true };
  },

  async getStudentDashboard(filters = {}) {
    const { data, error } = await supabase.rpc('get_my_summative_exam_dashboard', {
      target_year: filters.academicYearId || null,
      target_semester: filters.semesterId || null,
    });
    if (error) throw rpcError(error, 'Unable to load summative examination booking.');
    const dashboard = data || { student: null, period: null, units: [] };
    if (dashboard.period?.academic_year_id && dashboard.period?.semester_id) {
      dashboard.booking_window = await this.getBookingWindow(dashboard.period.academic_year_id, dashboard.period.semester_id);
    }
    return dashboard;
  },

  async bookUnit(unitRegistrationId) {
    const { data, error } = await supabase.rpc('book_summative_exam', {
      target_unit_registration: unitRegistrationId,
    });
    if (error) throw rpcError(error, 'Unable to book this examination.');
    return data;
  },

  async getStudentResitDashboard() {
    const { data, error } = await supabase.rpc('get_my_resit_exam_dashboard');
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Resit examination booking is not installed in the database yet. Apply migration 20260101000084 and refresh.');
      }
      throw new Error(error.message || 'Unable to load resit examination options.');
    }
    const dashboard = data || { student: null, period: null, resit_units: [], booked_count: 0, maximum_bookings: 10, remaining_bookings: 10 };
    if (dashboard.period?.academic_year_id && dashboard.period?.semester_id) {
      dashboard.booking_window = await this.getBookingWindow(dashboard.period.academic_year_id, dashboard.period.semester_id);
    }
    return dashboard;
  },

  async bookResitUnit(unitRegistrationId) {
    const { data, error } = await supabase.rpc('book_summative_resit_exam', {
      target_unit_registration: unitRegistrationId,
    });
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Resit examination booking is not installed in the database yet. Apply migration 20260101000084 and refresh.');
      }
      throw new Error(error.message || 'Unable to book this resit examination.');
    }
    return data;
  },

  async getDepartmentBookings(filters = {}) {
    const rpcFilters = {
      target_year: filters.academicYearId || null,
      target_semester: filters.semesterId || null,
    };
    const [{ data, error }, attendanceResult] = await Promise.all([
      supabase.rpc('get_department_summative_exam_bookings', rpcFilters),
      supabase.rpc('get_department_summative_attendance_roster', rpcFilters),
    ]);
    if (error) throw rpcError(error, 'Unable to load department examination bookings.');
    const attendanceEntries = Array.isArray(attendanceResult?.data?.entries) ? attendanceResult.data.entries : [];
    const attendanceByBooking = new Map(attendanceEntries.map(row => [String(row.booking_id), row]));
    return {
      ...(data || { scope: null }),
      bookings: (Array.isArray(data?.bookings) ? data.bookings : []).map(row => ({
        ...row,
        ...(attendanceByBooking.get(String(row.booking_id)) || {
          attendance_percentage: null,
          required_attendance: 70,
          attendance_recorded: false,
          attendance_recorded_at: null,
        }),
      })),
    };
  },

  async getTrainerMarksRoster(filters = {}) {
    const rpcFilters = {
      target_year: filters.academicYearId || null,
      target_semester: filters.semesterId || null,
      target_unit: filters.unitId || null,
    };
    const [{ data, error }, attendanceResult] = await Promise.all([
      supabase.rpc('get_trainer_summative_marks_roster', rpcFilters),
      supabase.rpc('get_trainer_summative_attendance_roster', rpcFilters),
    ]);
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Summative marks entry is not installed in the database yet. Apply migration 20260101000055 and refresh.');
      }
      throw new Error(error.message || 'Unable to load summative marks roster.');
    }
    if (attendanceResult?.error) {
      if (attendanceResult.error.code === 'PGRST202' || /Could not find the function/i.test(attendanceResult.error.message || '')) {
        throw new Error('Trainer attendance entry is not installed in the database yet. Apply migration 20260101000065 (and 20260101000066) and refresh.');
      }
      throw new Error(attendanceResult.error.message || 'Unable to load saved trainer attendance.');
    }
    const attendanceEntries = Array.isArray(attendanceResult?.data?.entries) ? attendanceResult.data.entries : [];
    const attendanceByBooking = new Map(attendanceEntries.map(row => [String(row.booking_id), row]));
    return {
      ...(data || {}),
      entries: (Array.isArray(data?.entries) ? data.entries : []).map(row => ({
        ...row,
        ...(attendanceByBooking.get(String(row.booking_id)) || {
          attendance_percentage: null,
          required_attendance: 70,
          attendance_recorded: false,
          attendance_recorded_at: null,
        }),
      })),
    };
  },

  async getTrainerAssignedUnits() {
    const { data, error } = await supabase.rpc('get_trainer_summative_assigned_units');
    if (error) {
      if (error.code === 'PGRST202' || error.code === '42883' || /Could not find the function|does not exist/i.test(error.message || '')) {
        return { units: [] };
      }
      throw new Error(error.message || 'Unable to load your assigned summative units.');
    }
    return data || { units: [] };
  },

  async saveTrainerMark({ bookingId, rawMarks, remarks = '' }) {
    const { data, error } = await supabase.rpc('save_trainer_summative_mark', {
      target_booking_id: bookingId,
      target_raw_marks: Number(rawMarks),
      target_remarks: remarks || null,
    });
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Summative marks entry is not installed in the database yet. Apply migration 20260101000055 and refresh.');
      }
      throw new Error(error.message || 'Unable to save summative mark.');
    }
    return data;
  },

  async saveTrainerAttendance({ bookingId, attendancePercentage }) {
    const { data, error } = await supabase.rpc('save_trainer_summative_attendance', {
      target_booking_id: bookingId,
      target_attendance: Number(attendancePercentage),
    });
    if (error) {
      if (error.code === 'PGRST202' || error.code === '42883' || /Could not find the function|does not exist/i.test(error.message || '')) {
        throw new Error('Trainer attendance entry is not installed in the database yet. Apply migration 20260101000065 and refresh.');
      }
      throw new Error(error.message || 'Unable to save class attendance.');
    }
    return data;
  },

  async submitResultBatch({ academicYearId, semesterId, programId, unitId, programLevel = null, moduleNumber = null }) {
    const { data, error } = await supabase.rpc('submit_summative_result_batch', {
      target_year: academicYearId,
      target_semester: semesterId,
      target_program: programId || null,
      target_unit: unitId || null,
      target_program_level: programLevel == null ? null : Number(programLevel),
      target_module_number: moduleNumber == null ? null : Number(moduleNumber),
    });
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Summative result approvals are not installed in the database yet. Apply migration 20260101000059 and refresh.');
      }
      throw new Error(error.message || 'Unable to submit the result batch for HoD approval.');
    }
    return data;
  },

  async getResultApprovalQueue(filters = {}) {
    const { data, error } = await supabase.rpc('get_summative_result_approval_queue', {
      target_year: filters.academicYearId || null,
      target_semester: filters.semesterId || null,
      target_status: filters.status || null,
    });
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Summative result approvals are not installed in the database yet. Apply migration 20260101000059 and refresh.');
      }
      throw new Error(error.message || 'Unable to load the summative approval queue.');
    }
    return data || { batches: [] };
  },

  async getResultUnitCatalog(filters = {}) {
    const { data, error } = await supabase.rpc('get_summative_result_unit_catalog', {
      target_year: filters.academicYearId || null,
      target_semester: filters.semesterId || null,
      target_program: filters.programId || null,
      target_program_level: filters.programLevel == null ? null : Number(filters.programLevel),
      target_module_number: filters.moduleNumber == null ? null : Number(filters.moduleNumber),
    });
    if (error) {
      if (error.code === 'PGRST202' || error.code === '42883' || /Could not find the function|does not exist/i.test(error.message || '')) {
        // Older databases do not have the dedicated catalogue RPC yet. Use the
        // existing scoped-unit endpoint so a consolidated sheet still contains
        // every unit configured for the programme/module, not only submitted
        // result batches.
        try {
          const { data: scopedRows, error: scopedError } = await supabase.rpc('get_scoped_units');
          if (scopedError) return { units: [] };
          const requestedProgram = String(filters.programId || '').toLowerCase();
          const requestedLevel = filters.programLevel == null ? null : Number(filters.programLevel);
          const requestedModule = filters.moduleNumber == null ? null : Number(filters.moduleNumber);
          const units = (scopedRows || [])
            .map(row => row?.unit || row)
            .filter(unit => {
              if (!unit || unit.is_active === false) return false;
              const program = unit.program || {};
              const programValues = [unit.program_id, program.id, program.code]
                .filter(value => value != null)
                .map(value => String(value).toLowerCase());
              const programMatches = !requestedProgram || programValues.includes(requestedProgram);
              const levelValue = unit.program_level ?? program.level;
              const moduleValue = unit.module_in_level ?? unit.module ?? program.module;
              const levelMatches = requestedLevel == null || levelValue == null || Number(levelValue) === requestedLevel;
              const moduleMatches = requestedModule == null || moduleValue == null || Number(moduleValue) === requestedModule;
              return programMatches && levelMatches && moduleMatches;
            })
            .map(unit => ({
              unit_id: unit.id,
              unit_code: unit.code,
              unit_name: unit.name,
            }))
            .filter(unit => unit.unit_id != null || unit.unit_code);
          return { units };
        } catch (_) {
          return { units: [] };
        }
      }
      throw new Error(error.message || 'Unable to load the programme unit catalogue.');
    }
    return data || { units: [] };
  },

  async getAdmittedSummativeRoster(filters = {}) {
    const { data, error } = await supabase.rpc('get_summative_admitted_roster', {
      target_year: filters.academicYearId || null,
      target_semester: filters.semesterId || null,
      target_program: filters.programId || null,
      target_unit: filters.unitId || null,
      target_program_level: filters.programLevel == null ? null : Number(filters.programLevel),
      target_module_number: filters.moduleNumber == null ? null : Number(filters.moduleNumber),
    });
    if (error) {
      if (error.code === 'PGRST202' || error.code === '42883' || /Could not find the function|does not exist/i.test(error.message || '')) {
        throw new Error('The admitted Senate roster is not installed in the database yet. Apply migration 20260923000008 and refresh.');
      }
      throw new Error(error.message || 'Unable to load the admitted Senate roster.');
    }
    return data || { entries: [] };
  },

  async advanceResultBatch({ batchId, action, note = '' }) {
    const isDeanRecordingSenate = action === 'record_senate_approval';
    const { data, error } = isDeanRecordingSenate
      ? await supabase.rpc('record_summative_senate_approval', {
        target_batch_id: batchId,
        target_note: note || null,
      })
      : await supabase.rpc('advance_summative_result_batch', {
        target_batch_id: batchId,
        target_action: action,
        target_note: note || null,
      });
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error(`Summative result approvals are not installed in the database yet. Apply migration ${isDeanRecordingSenate ? '20260101000068' : '20260101000059'} and refresh.`);
      }
      throw new Error(error.message || 'Unable to update the result approval.');
    }
    return data;
  },

  async getAcademicProgress(studentId = null) {
    const { data, error } = await supabase.rpc('get_student_academic_progress', {
      target_student: studentId || null,
    });
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Historical academic progress is not installed in the database yet. Apply migration 20260101000069 and refresh.');
      }
      if (/COALESCE types text and uuid cannot be matched/i.test(error.message || '')) {
        throw new Error('Historical academic progress needs the UUID/text compatibility repair. Apply migration 20260101000099 and refresh.');
      }
      throw new Error(error.message || 'Unable to load historical academic progress.');
    }
    return data || { student: null, rows: [], unit_count: 0, module_count: 0, programme_end_module: 0 };
  },

  async getScopedAcademicProgress(programId = null) {
    const { data, error } = await supabase.rpc('get_scoped_academic_progress_with_intake', {
      target_program: programId || null,
    });
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Staff historical progress is not installed in the database yet. Apply migrations 20260101000070 and 20260101000074, then refresh.');
      }
      if (/COALESCE types text and uuid cannot be matched/i.test(error.message || '')) {
        throw new Error('Staff historical progress needs the UUID/text compatibility repair. Apply migration 20260101000099 and refresh.');
      }
      throw new Error(error.message || 'Unable to load scoped historical academic progress.');
    }
    return data || { scope: null, students: [] };
  },

  async getMyProvisionalResults(filters = {}) {
    const { data, error } = await supabase.rpc('get_my_provisional_summative_results', {
      target_year: filters.academicYearId || null,
      target_semester: filters.semesterId || null,
    });
    if (error) {
      if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message || '')) {
        throw new Error('Provisional semester results are not installed in the database yet. Apply migration 20260101000059 and refresh.');
      }
      throw new Error(error.message || 'Unable to load provisional semester results.');
    }
    return data || { results: [] };
  },
};

export default summativeExamService;
